// Iter-191k — Cross-mission profitability dashboard (CEO / ADMIN only).
// Surfaces: global KPIs, top-5 / bottom-5 margins, watchlist of missions drifting from
// their projected margin. Helps the CEO spot leaks before billing/CRA validation cycles.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowDown, ArrowUp, Building2, Calendar, Loader2, TrendingDown, TrendingUp } from "lucide-react";
import CabinetKpis from "@/components/CabinetKpis";

const fmtMad = (n) =>
  n === null || n === undefined ? "—" : `${Number(n).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} MAD`;

const pctTone = (pct) => {
  if (pct === null || pct === undefined) return "text-zinc-500";
  if (pct >= 30) return "text-emerald-700";
  if (pct >= 15) return "text-amber-700";
  return "text-red-700";
};

const deltaBadge = (delta) => {
  if (delta === null || delta === undefined) return null;
  // Iter-191k — only surface badge when |delta| ≥ 5 pp; smaller deltas are noise.
  if (Math.abs(delta) < 5) return null;
  // Iter-193c — portfolio row now shows projection by default; delta_pp = actual − projected.
  // Negative = actual underperforming the plan (bad). Positive = actual above plan (good).
  if (delta <= -5) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-red-50 text-red-700 rounded" title="Le réel (CRA) sous-performe la projection">
        <TrendingDown size={10} /> Réel {delta} pp
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded" title="Le réel (CRA) sur-performe la projection">
      <TrendingUp size={10} /> Réel +{delta} pp
    </span>
  );
};

export default function ProfitabilityPortfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeClosed, setIncludeClosed] = useState(false);
  // Iter-193b — monthly view (6 last completed months)
  const [monthly, setMonthly] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/profitability/portfolio${includeClosed ? "?include_closed=true" : ""}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement du portfolio");
    } finally {
      setLoading(false);
    }
  };
  const loadMonthly = async () => {
    setMonthlyLoading(true);
    try {
      const r = await api.get("/profitability/portfolio/monthly?months=6");
      setMonthly(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement mensuel");
    } finally {
      setMonthlyLoading(false);
    }
  };
  useEffect(() => { load(); }, [includeClosed]); // eslint-disable-line
  useEffect(() => { loadMonthly(); }, []); // eslint-disable-line

  if (loading && !data) {
    return (
      <div className="px-6 py-12 flex items-center gap-2 text-zinc-500">
        <Loader2 size={16} className="animate-spin" /> Chargement du portfolio…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto" data-testid="profitability-portfolio">
      {/* Iter-193q — Cabinet profitability section (above the portfolio block). */}
      <section className="mb-10" data-testid="cabinet-profitability-section">
        <div className="mb-4">
          <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
            <Building2 size={20} className="text-zinc-700" />
            Rentabilité du cabinet
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Profit net consolidé sur le mois courant, les 6 et 12 derniers mois.
            Inclut revenus facturés, coûts missions, dépenses internes et NDF.
          </p>
        </div>
        <CabinetKpis />
      </section>

      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Rentabilité du portfolio</h2>
          <p className="text-sm text-zinc-500 mt-1">
            Vue <strong className="text-zinc-700">projection</strong> par défaut (budgets &amp; CJM planifiés).
            Le delta vs réel (CRA validés) est indiqué sur chaque mission.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-zinc-700">
          <input
            type="checkbox"
            checked={includeClosed}
            onChange={(e) => setIncludeClosed(e.target.checked)}
            data-testid="portfolio-include-closed"
          />
          Inclure missions closes
        </label>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <Kpi label="Missions" value={data.kpi.n_missions} testid="kpi-n-missions" />
        <Kpi label="CA total" value={fmtMad(data.kpi.revenue_total_mad)} testid="kpi-revenue" />
        <Kpi label="Coût total" value={fmtMad(data.kpi.cost_total_mad)} testid="kpi-cost" />
        <Kpi label="Marge totale" value={fmtMad(data.kpi.margin_total_mad)} tone={pctTone(data.kpi.weighted_margin_pct)} testid="kpi-margin" />
        <Kpi label="Marge pondérée" value={data.kpi.weighted_margin_pct == null ? "—" : `${data.kpi.weighted_margin_pct}%`} tone={pctTone(data.kpi.weighted_margin_pct)} testid="kpi-weighted-pct" />
      </div>

      {/* Watchlist */}
      {data.watchlist.length > 0 && (
        <div className="mb-8 bg-red-50/40 border border-red-200 rounded-lg overflow-hidden" data-testid="watchlist-block">
          <div className="px-5 py-3 bg-red-100/50 border-b border-red-200 flex items-center gap-2">
            <AlertTriangle size={14} className="text-red-700" />
            <span className="font-heading font-semibold text-red-900">Missions à surveiller</span>
            <span className="text-xs font-mono text-red-700">{data.watchlist.length}</span>
          </div>
          <div className="divide-y divide-red-100">
            {data.watchlist.map((r) => <MissionRow key={r.mission_id} r={r} testidPrefix="watchlist" />)}
          </div>
          <div className="px-5 py-2 text-[11px] text-red-700/80 bg-red-50">
            Seuils : marge actuelle &lt; {data.thresholds.watchlist_min_pct}% OU dérive ≥ {Math.abs(data.thresholds.watchlist_delta_pp)} pp.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 5 most profitable */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="top-margin-block">
          <div className="px-5 py-3 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
            <ArrowUp size={14} className="text-emerald-700" />
            <span className="font-heading font-semibold text-emerald-900">Top 5 marges</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {data.top_margin.length === 0 && (
              <div className="px-5 py-10 text-sm text-zinc-500 text-center">Aucune donnée.</div>
            )}
            {data.top_margin.map((r) => <MissionRow key={r.mission_id} r={r} testidPrefix="top" />)}
          </div>
        </div>

        {/* Bottom 5 */}
        <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="bottom-margin-block">
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 flex items-center gap-2">
            <ArrowDown size={14} className="text-red-700" />
            <span className="font-heading font-semibold text-red-900">Bottom 5 marges</span>
          </div>
          <div className="divide-y divide-zinc-100">
            {data.bottom_margin.length === 0 && (
              <div className="px-5 py-10 text-sm text-zinc-500 text-center">Aucune donnée.</div>
            )}
            {data.bottom_margin.map((r) => <MissionRow key={r.mission_id} r={r} testidPrefix="bottom" />)}
          </div>
        </div>
      </div>

      {/* Iter-193b — Monthly profitability (6 last completed months) */}
      <MonthlyProfitabilitySection monthly={monthly} loading={monthlyLoading} />
    </div>
  );
}

function MissionRow({ r, testidPrefix }) {
  const pct = r.margin_pct;
  return (
    <div className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`${testidPrefix}-row-${r.mission_id}`}>
      <div className="min-w-0">
        <Link to={`/missions/${r.mission_id}`} className="font-medium text-sm text-zinc-900 hover:underline truncate block">
          {r.title}
        </Link>
        <div className="text-[10px] text-zinc-500 font-mono mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="px-1.5 py-0.5 bg-zinc-100 rounded">{r.type}</span>
          <span>{r.currency || "MAD"}</span>
          {deltaBadge(r.delta_pp)}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-sm font-mono font-bold ${pctTone(pct)}`}>{pct == null ? "—" : `${pct}%`}</div>
        <div className="text-[10px] text-zinc-500 font-mono">CA {fmtMad(r.revenue)} · Coût {fmtMad(r.cost)}</div>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint, tone, testid }) {
  return (
    <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">{label}</div>
      <div className={`text-lg font-bold font-mono mt-1 ${tone || "text-zinc-900"}`}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 font-mono mt-0.5">{hint}</div>}
    </div>
  );
}


// Iter-193b — Monthly profitability (6 last completed months).
// Displays:
//   1. A global per-month strip (CA / Coût / Marge / Marge %).
//   2. A per-mission breakdown table (one row per mission, one column per month).
function MonthlyProfitabilitySection({ monthly, loading }) {
  if (loading && !monthly) {
    return (
      <div className="mt-10 flex items-center gap-2 text-zinc-500 text-sm" data-testid="monthly-profitability-loading">
        <Loader2 size={14} className="animate-spin" /> Chargement du détail mensuel…
      </div>
    );
  }
  if (!monthly) return null;
  const months = monthly.months || [];
  const fmtYm = (ym) => {
    if (!ym || typeof ym !== "string") return ym;
    const [y, m] = ym.split("-");
    const labels = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
    return `${labels[Number(m) - 1] || m} ${y.slice(2)}`;
  };

  return (
    <div className="mt-10 space-y-6" data-testid="monthly-profitability-section">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Calendar size={16} className="text-zinc-700" />
            Rentabilité mensuelle — 6 derniers mois échus
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Vue cumulée (global) et détaillée par mission. Mois courant exclu (CRA non finalisé).
          </p>
        </div>
      </div>

      {/* Global per-month strip */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="monthly-global-block">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-zinc-50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              <th className="px-4 py-3">Mois</th>
              <th className="px-4 py-3 text-right">Missions actives</th>
              <th className="px-4 py-3 text-right">CA</th>
              <th className="px-4 py-3 text-right">Coût</th>
              <th className="px-4 py-3 text-right">Marge</th>
              <th className="px-4 py-3 text-right">Marge %</th>
            </tr>
          </thead>
          <tbody>
            {(monthly.global || []).map((g) => (
              <tr key={g.ym} className="border-t border-zinc-100" data-testid={`monthly-global-row-${g.ym}`}>
                <td className="px-4 py-3 font-mono text-xs text-zinc-700">{fmtYm(g.ym)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-zinc-700">{g.n_missions}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-zinc-900">{fmtMad(g.revenue)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs text-zinc-900">{fmtMad(g.cost)}</td>
                <td className={`px-4 py-3 text-right font-mono text-xs font-semibold ${g.margin >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                  {fmtMad(g.margin)}
                </td>
                <td className={`px-4 py-3 text-right font-mono text-xs font-semibold ${pctTone(g.margin_pct)}`}>
                  {g.margin_pct == null ? "—" : `${g.margin_pct}%`}
                </td>
              </tr>
            ))}
            {(monthly.global || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-zinc-500">Aucune donnée mensuelle disponible.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Per-mission monthly breakdown */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto" data-testid="monthly-by-mission-block">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-zinc-50">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              <th className="px-4 py-3">Mission</th>
              {months.map((ym) => (
                <th key={ym} className="px-3 py-3 text-right">{fmtYm(ym)}</th>
              ))}
              <th className="px-4 py-3 text-right border-l border-zinc-200">Cumul</th>
            </tr>
          </thead>
          <tbody>
            {(monthly.by_mission || []).map((m) => (
              <tr key={m.mission_id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`monthly-mission-row-${m.mission_id}`}>
                <td className="px-4 py-3 min-w-[220px]">
                  <Link to={`/missions/${m.mission_id}`} className="font-medium text-sm text-zinc-900 hover:underline truncate block">
                    {m.title}
                  </Link>
                  <div className="text-[10px] text-zinc-500 font-mono mt-0.5 flex items-center gap-2">
                    <span className="px-1.5 py-0.5 bg-zinc-100 rounded">{m.type}</span>
                    <span>{m.currency || "MAD"}</span>
                  </div>
                </td>
                {m.by_month.map((b) => (
                  <td key={b.ym} className="px-3 py-3 text-right font-mono text-[11px] whitespace-nowrap" data-testid={`monthly-cell-${m.mission_id}-${b.ym}`}>
                    {b.days_count > 0 ? (
                      <>
                        <div className={`font-semibold ${pctTone(b.margin_pct)}`}>
                          {b.margin_pct == null ? "—" : `${b.margin_pct}%`}
                        </div>
                        <div className="text-[10px] text-zinc-500">{fmtMad(b.margin)}</div>
                      </>
                    ) : (
                      <span className="text-zinc-300">—</span>
                    )}
                  </td>
                ))}
                <td className="px-4 py-3 text-right font-mono text-xs border-l border-zinc-200 whitespace-nowrap">
                  <div className={`font-bold ${pctTone(m.avg_margin_pct)}`}>
                    {m.avg_margin_pct == null ? "—" : `${m.avg_margin_pct}%`}
                  </div>
                  <div className="text-[10px] text-zinc-500">{fmtMad(m.total_margin)}</div>
                </td>
              </tr>
            ))}
            {(monthly.by_mission || []).length === 0 && (
              <tr>
                <td colSpan={months.length + 2} className="px-4 py-10 text-center text-sm text-zinc-500">
                  Aucune mission n'a de CRA validé sur la fenêtre. Soumettez des CRA puis revenez.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
